import { useState } from 'react'
import ReactMarkdown from 'react-markdown'

interface Todo {
  id: string
  question: string
  options: string[]
  context?: string
  idea?: string
  blocking?: string[]
  agent_reasoning?: string
  source?: string
  claim_fidelity?: number | null
  created_at?: string
  priority?: string
  todo_type?: string
  // Phase 2 fields (optional — gracefully absent on existing TODOs)
  failure_mode?: string
  expected_result?: string
  suggested_fix?: string
  agent_proposed_fix?: string
  test_id?: string
  canon_ref?: string
  severity?: string
  origin?: string
  // Phase 3 fields: 3-layer enrichment (optional — absent on pre-enrichment TODOs)
  briefing?: string
  enriched_options?: string[]
  claim_context?: string
}

interface TodoCardProps {
  todo: Todo
  onResolve: (todoId: string, selectedOption: number | null, notes?: string) => Promise<void>
  onMarkComplete?: (todoId: string) => Promise<void>
  variant?: 'todo' | 'proposition'
}

// Relative time from ISO string (e.g., "3 days ago", "2 hours ago")
function relativeTime(iso: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  if (isNaN(then)) return ''
  const diff = now - then
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

// Priority badge colors
function priorityStyle(p: string): string {
  switch (p) {
    case 'P0': return 'bg-red-950 text-red-400 border-red-800'
    case 'P1': return 'bg-orange-950 text-orange-400 border-orange-800'
    case 'P2': return 'bg-blue-950 text-blue-400 border-blue-800'
    case 'P3': return 'bg-gray-800 text-gray-500 border-gray-700'
    default: return 'bg-gray-800 text-gray-500 border-gray-700'
  }
}

// Severity badge colors
function severityStyle(s: string): string {
  switch (s.toUpperCase()) {
    case 'CRITICAL': return 'bg-red-950 text-red-400 border-red-800'
    case 'HIGH': return 'bg-orange-950 text-orange-400 border-orange-800'
    case 'MEDIUM': return 'bg-yellow-950 text-yellow-400 border-yellow-800'
    case 'LOW': return 'bg-gray-800 text-gray-500 border-gray-700'
    default: return 'bg-gray-800 text-gray-500 border-gray-700'
  }
}

export default function TodoCard({ todo, onResolve, onMarkComplete, variant = 'todo' }: TodoCardProps) {
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [responseText, setResponseText] = useState('')
  const [isResolving, setIsResolving] = useState(false)

  const isProposition = variant === 'proposition'
  const hasTestDetails = !!(todo.failure_mode || todo.expected_result || todo.suggested_fix)
  // Use enriched options (with consequence annotations) when available, fall back to raw
  const displayOptions = todo.enriched_options?.length ? todo.enriched_options : todo.options

  const handleResolve = async () => {
    // Require at least an option selection or freeform text
    if (selectedOption === null && !responseText.trim()) return
    setIsResolving(true)
    try {
      await onResolve(todo.id, selectedOption, responseText.trim() || undefined)
    } catch (error) {
      console.error('Failed to resolve:', error)
      setIsResolving(false)
    }
  }

  const handleMarkComplete = async () => {
    if (!onMarkComplete) return
    setIsResolving(true)
    try {
      await onMarkComplete(todo.id)
    } catch (error) {
      console.error('Failed to mark complete:', error)
      setIsResolving(false)
    }
  }

  // Format fidelity as percentage
  const fidelityPct = todo.claim_fidelity != null
    ? `${Math.round(todo.claim_fidelity * 100)}%`
    : null

  // Fidelity color based on confidence level
  const fidelityColor = todo.claim_fidelity != null
    ? todo.claim_fidelity >= 0.5
      ? 'bg-green-950 text-green-400 border-green-800'
      : todo.claim_fidelity >= 0.3
        ? 'bg-yellow-950 text-yellow-400 border-yellow-800'
        : 'bg-red-950 text-red-400 border-red-800'
    : ''

  return (
    <div className={`rounded-lg border p-6 ${
      isProposition
        ? 'bg-gray-900 border-purple-900/50'
        : 'bg-gray-900 border-gray-800'
    }`}>
      {/* Header: Badges + ID + Timestamp */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {todo.idea && (
            <span className={`px-2 py-1 text-xs font-medium rounded border ${
              isProposition
                ? 'bg-purple-950 text-purple-400 border-purple-800'
                : 'bg-blue-950 text-blue-400 border-blue-800'
            }`}>
              {todo.idea}
            </span>
          )}
          {todo.priority && (
            <span className={`px-2 py-1 text-xs font-medium rounded border ${priorityStyle(todo.priority)}`}>
              {todo.priority}
            </span>
          )}
          {todo.severity && (
            <span className={`px-2 py-1 text-xs font-medium rounded border ${severityStyle(todo.severity)}`}>
              {todo.severity}
            </span>
          )}
          {isProposition && fidelityPct && (
            <span className={`px-2 py-1 text-xs font-medium rounded border ${fidelityColor}`}>
              Fidelity: {fidelityPct}
            </span>
          )}
          {todo.source && (
            <span className="px-2 py-1 bg-gray-800 text-gray-500 text-xs rounded border border-gray-700">
              {todo.source.replace(/_/g, ' ')}
            </span>
          )}
          {todo.canon_ref && (
            <span className="px-2 py-1 bg-gray-800 text-gray-400 text-xs font-mono rounded border border-gray-700">
              {todo.canon_ref}
            </span>
          )}
          {todo.blocking && todo.blocking.length > 0 && (
            <span className="px-2 py-1 bg-yellow-950 text-yellow-400 text-xs font-medium rounded border border-yellow-800 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Blocking: {todo.blocking.join(', ')}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-3">
          <span className="px-2 py-1 bg-gray-800 text-gray-400 text-xs font-mono rounded">
            {todo.id}
          </span>
          {todo.created_at && (
            <span className="text-xs text-gray-600" title={todo.created_at}>
              {relativeTime(todo.created_at)}
            </span>
          )}
        </div>
      </div>

      {/* Layer 1: Briefing — quick summary for zero-context reader */}
      {todo.briefing && (
        <p className="text-sm text-gray-400 leading-relaxed mb-2">{todo.briefing}</p>
      )}

      {/* Question */}
      <h3 className="text-lg font-semibold text-white mb-3">{todo.question}</h3>

      {/* Context */}
      {todo.context && (
        <div className="mb-4 p-3 bg-gray-950 rounded border border-gray-800">
          <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{todo.context}</p>
        </div>
      )}

      {/* Test Failure Details (Phase 2 — only shown when fields are present) */}
      {hasTestDetails && (
        <details className="mb-4 p-3 bg-gray-950 rounded border border-gray-800" open>
          <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-300 font-medium">
            Test Details{todo.test_id ? ` — ${todo.test_id}` : ''}
          </summary>
          <div className="mt-3 space-y-3">
            {todo.failure_mode && (
              <div>
                <div className="text-xs font-medium text-red-400 mb-1">What Failed</div>
                <div className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{todo.failure_mode}</div>
              </div>
            )}
            {todo.expected_result && (
              <div>
                <div className="text-xs font-medium text-green-400 mb-1">Expected</div>
                <div className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{todo.expected_result}</div>
              </div>
            )}
            {todo.suggested_fix && (
              <div>
                <div className="text-xs font-medium text-blue-400 mb-1">Suggested Fix</div>
                <div className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{todo.suggested_fix}</div>
              </div>
            )}
            {todo.agent_proposed_fix && (
              <div>
                <div className="text-xs font-medium text-purple-400 mb-1">Agent's Analysis</div>
                <div className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{todo.agent_proposed_fix}</div>
              </div>
            )}
          </div>
        </details>
      )}

      {/* Agent Reasoning */}
      {todo.agent_reasoning && (
        <details className="mb-4 p-3 bg-gray-950 rounded border border-gray-800">
          <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-400">
            Agent Reasoning
          </summary>
          <div className="mt-2 text-sm text-gray-400 leading-relaxed prose prose-sm prose-invert max-w-none
            prose-headings:text-gray-300 prose-headings:text-sm prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1
            prose-strong:text-gray-300 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
            <ReactMarkdown>{todo.agent_reasoning}</ReactMarkdown>
          </div>
        </details>
      )}

      {/* Full Report — aggregates all context for zero-context reader (Jacob's UX feedback) */}
      {(() => {
        const sections: string[] = []
        if (todo.context) sections.push('## Context\n' + todo.context)
        if (todo.agent_reasoning) sections.push('## Analysis\n' + todo.agent_reasoning)
        if (todo.failure_mode) sections.push('## What Failed\n' + todo.failure_mode)
        if (todo.expected_result) sections.push('## Expected Result\n' + todo.expected_result)
        if (todo.suggested_fix) sections.push('## Suggested Fix\n' + todo.suggested_fix)
        if (todo.agent_proposed_fix) sections.push("## Agent's Proposed Fix\n" + todo.agent_proposed_fix)
        if (todo.claim_context) sections.push('## Related Claims\n```\n' + todo.claim_context + '\n```')
        if (sections.length < 2) return null
        return (
          <details className="mb-4 p-3 bg-gray-950 rounded border border-gray-800">
            <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-400 font-medium flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Full Report ({sections.length} sections)
            </summary>
            <div className="mt-2 text-sm text-gray-400 leading-relaxed prose prose-sm prose-invert max-w-none
              prose-headings:text-gray-300 prose-headings:text-sm prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1
              prose-strong:text-gray-300 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0
              prose-code:text-gray-300 prose-pre:bg-gray-900 prose-pre:border prose-pre:border-gray-700">
              <ReactMarkdown>{sections.join('\n\n---\n\n')}</ReactMarkdown>
            </div>
          </details>
        )
      })()}

      {/* Layer 4: Proposition Graph Context */}
      {todo.claim_context && (
        <details className="mb-4 p-3 bg-gray-950 rounded border border-gray-800">
          <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-400 font-medium">
            Related Propositions
          </summary>
          <pre className="mt-2 text-sm text-gray-400 leading-relaxed whitespace-pre-wrap font-mono">{todo.claim_context}</pre>
        </details>
      )}

      {/* Options + freeform response */}
      {todo.options.length > 0 && (
        <div className="mb-4 space-y-2">
          {displayOptions.map((option, index) => (
            <button
              key={index}
              onClick={() => setSelectedOption(selectedOption === index ? null : index)}
              disabled={isResolving}
              className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                selectedOption === index
                  ? isProposition
                    ? 'border-purple-600 bg-purple-950/30'
                    : 'border-green-600 bg-green-950/30'
                  : 'border-gray-700 bg-gray-950 hover:border-gray-600'
              } ${isResolving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  selectedOption === index
                    ? isProposition
                      ? 'border-purple-600 bg-purple-600'
                      : 'border-green-600 bg-green-600'
                    : 'border-gray-600'
                }`}>
                  {selectedOption === index && (
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <div className="text-xs font-medium text-gray-500 mb-0.5">Option {index + 1}</div>
                  <div className="text-sm text-gray-300 leading-relaxed">{option}</div>
                </div>
              </div>
            </button>
          ))}
          {/* Freeform response — compact inline input after the options */}
          <div className="flex items-start gap-3 p-3 rounded-lg border-2 border-gray-700 bg-gray-950">
            <div className="mt-1 w-4 h-4 rounded-sm border-2 border-gray-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              disabled={isResolving}
              placeholder={isProposition
                ? "Or type your reasoning..."
                : "Or type your own response..."}
              className="flex-1 px-0 py-0 bg-transparent border-0 text-sm text-gray-300 placeholder-gray-600 focus:outline-none resize-none"
              rows={1}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement
                target.style.height = 'auto'
                target.style.height = target.scrollHeight + 'px'
              }}
            />
          </div>
        </div>
      )}

      {/* Fallback freeform when no options exist */}
      {todo.options.length === 0 && (
        <div className="mb-4">
          <textarea
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            disabled={isResolving}
            placeholder="Type your response..."
            className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded text-sm text-gray-300 placeholder-gray-600 focus:border-gray-500 focus:outline-none resize-none"
            rows={2}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleResolve}
          disabled={isResolving || (selectedOption === null && !responseText.trim())}
          className={`px-6 py-2.5 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2 ${
            isProposition
              ? 'bg-purple-600 hover:bg-purple-700'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {isResolving ? (
            <>
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Resolving...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {isProposition ? 'Submit Decision' : 'Resolve'}
            </>
          )}
        </button>
        {onMarkComplete && (
          <button
            onClick={handleMarkComplete}
            disabled={isResolving}
            className="px-4 py-2.5 text-gray-400 hover:text-gray-300 border border-gray-700 hover:border-gray-600 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Mark Complete
          </button>
        )}
        {selectedOption !== null && (
          <button
            onClick={() => setSelectedOption(null)}
            disabled={isResolving}
            className="px-4 py-2.5 text-gray-500 hover:text-gray-400 text-sm"
          >
            Clear selection
          </button>
        )}
      </div>
    </div>
  )
}
